import type { Dict, Mixpanel } from 'mixpanel-browser';

/**
 * Standard UTM keywords Mixpanel captures natively (mirrors mixpanel-browser's
 * CAMPAIGN_KEYWORDS). Kept as raw `utm_*` names so the registered super
 * properties are indistinguishable from the SDK's own capture.
 */
const MIXPANEL_CAMPAIGN_KEYWORDS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term'
] as const;

/**
 * Extract standard UTM params from a raw query string, keeping the native
 * `utm_*` keys.
 *
 * Mixpanel-browser reads these from `window.location` at init() and registers
 * them as super properties. Since init is deferred to browser idle time
 * (requestIdleCallback), the URL may already have changed (SPA navigation away
 * from the landing page) by the time it runs — dropping the source and yielding
 * "utm_source (not set)". Capturing the landing query synchronously and
 * registering it on init closes that race.
 */
export function getMixpanelCampaignParams(search: string): Dict {
	const params = new URLSearchParams(search);
	const campaign: Dict = {};

	for (const key of MIXPANEL_CAMPAIGN_KEYWORDS) {
		const value = params.get(key);
		if (value) campaign[key] = value;
	}

	return campaign;
}

export function attemptTrack(
	mixpanelInstance: Mixpanel,
	eventName: string,
	properties: Dict | undefined,
	retries: number = 0,
	maxRetries: number = 5,
	retryDelay: number = 1000
) {
	if (mixpanelInstance) {
		return mixpanelInstance.track(eventName, properties);
	}

	if (retries < maxRetries) {
		return setTimeout(() => {
			attemptTrack(mixpanelInstance, eventName, properties, retries + 1);
		}, retryDelay);
	}

	throw new Error(
		`Mixpanel track failed after ${maxRetries} retries for event: ${eventName}`
	);
}
